import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

export type BackgroundActivityRow = {
    trackId: string;
    name: string;
    syncing: boolean;
    syncProgressPercent: number | null;
    transcribing: boolean;
};

type BackgroundActivityCardProps = {
    visible: boolean;
    rows: BackgroundActivityRow[];
    syncingCount: number;
    transcribingCount: number;
    bottom: number;
    right?: number;
};

const labelForCounts = (syncingCount: number, transcribingCount: number): string => {
    const parts: string[] = [];
    if (syncingCount > 0) parts.push(`Uploading ${syncingCount}`);
    if (transcribingCount > 0) parts.push(`Transcribing ${transcribingCount}`);
    return parts.join(" | ");
};

const BackgroundActivityCard: React.FC<BackgroundActivityCardProps> = ({
    visible,
    rows,
    syncingCount,
    transcribingCount,
    bottom,
    right = 24,
}) => {
    const [expanded, setExpanded] = React.useState(false);

    React.useEffect(() => {
        if (!visible || rows.length === 0) {
            setExpanded(false);
        }
    }, [visible, rows.length]);

    if (!visible || rows.length === 0) {
        return null;
    }

    const collapsedLabel = labelForCounts(syncingCount, transcribingCount);

    return (
        <Box
            sx={{
                position: "fixed",
                right,
                bottom,
                zIndex: 1390,
                width: expanded ? "min(360px, calc(100vw - 32px))" : "auto",
                minWidth: expanded ? 300 : 0,
                backgroundColor: "#FFFFFF",
                border: "1px solid #E5E7EB",
                borderRadius: "12px",
                boxShadow: "0px 18px 50px rgba(15, 23, 42, 0.12)",
                overflow: "hidden",
            }}
        >
            <Box
                onClick={() => setExpanded((prev) => !prev)}
                sx={{
                    px: "10px",
                    py: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    backgroundColor: expanded ? "#F8FAFC" : "#FFFFFF",
                    borderBottom: expanded ? "1px solid #E5E7EB" : "none",
                }}
            >
                <CircularProgress size={14} thickness={7} sx={{ color: "#4B5563", flexShrink: 0 }} />
                <Typography
                    sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#1F2937",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        flex: 1,
                    }}
                >
                    {collapsedLabel}
                </Typography>
                <IconButton
                    size="small"
                    sx={{
                        width: 22,
                        height: 22,
                        color: "#6B7280",
                        transform: expanded ? "rotate(180deg)" : "none",
                        transition: "transform 0.2s ease",
                    }}
                >
                    <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            {expanded && (
                <Box
                    sx={{
                        maxHeight: "260px",
                        overflowY: "auto",
                        p: "8px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                    }}
                >
                    {rows.map((row) => (
                        <Box
                            key={row.trackId}
                            sx={{
                                p: "8px",
                                border: "1px solid #EEF2F7",
                                borderRadius: "10px",
                                backgroundColor: "#FFFFFF",
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    color: "#1F2937",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                                title={row.name}
                            >
                                {row.name}
                            </Typography>

                            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                {row.syncing && (
                                    <Tooltip title="Uploading to server" arrow placement="top">
                                        <Box
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                px: "6px",
                                                py: "2px",
                                                borderRadius: "999px",
                                                border: "1px solid #BFDBFE",
                                                backgroundColor: "#EFF6FF",
                                                color: "#1D4ED8",
                                            }}
                                        >
                                            <CloudUploadOutlinedIcon sx={{ fontSize: 10 }} />
                                            <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                                                Uploading
                                            </Typography>
                                            {row.syncProgressPercent !== null && (
                                                <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                                                    {row.syncProgressPercent}%
                                                </Typography>
                                            )}
                                        </Box>
                                    </Tooltip>
                                )}
                                {row.transcribing && (
                                    <Tooltip title="Transcribing" arrow placement="top">
                                        <Box
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                px: "6px",
                                                py: "2px",
                                                borderRadius: "999px",
                                                border: "1px solid #BAE6FD",
                                                backgroundColor: "#E0F2FE",
                                                color: "#0369A1",
                                            }}
                                        >
                                            <FormatQuoteIcon sx={{ fontSize: 10 }} />
                                            <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                                                Transcribing
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                )}
                            </Box>

                            {row.syncing && (
                                row.syncProgressPercent !== null ? (
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.max(1, Math.min(100, row.syncProgressPercent))}
                                        sx={{
                                            height: 4,
                                            borderRadius: 999,
                                            backgroundColor: "#E5E7EB",
                                            "& .MuiLinearProgress-bar": {
                                                borderRadius: 999,
                                                backgroundColor: "#3B82F6",
                                            },
                                        }}
                                    />
                                ) : (
                                    <LinearProgress
                                        sx={{
                                            height: 4,
                                            borderRadius: 999,
                                            backgroundColor: "#E5E7EB",
                                            "& .MuiLinearProgress-bar": {
                                                borderRadius: 999,
                                                backgroundColor: "#3B82F6",
                                            },
                                        }}
                                    />
                                )
                            )}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default BackgroundActivityCard;
